/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
  Flex,
  Heading,
  Button,
  FormControl,
  FormLabel,
  Checkbox,
  Text,
} from "@chakra-ui/react";
import { TimePicker } from "@progress/kendo-react-dateinputs";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { MdDeleteSweep } from "react-icons/md";
import { FaEdit } from "react-icons/fa";

export const SERVICE_WINDOW_DEFAULT = {
  startTime: null,
  endTime: null,
  dayOfWeek: [],
  secondStartTime: null,
  secondEndTime: null,
};

export const daysOfWeek = [
  "Everyday",
  "Weekdays",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

const toBackendTime = (date) => {
  if (!(date instanceof Date) || isNaN(date)) return null;
  return `${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes(),
  ).padStart(2, "0")}:00`;
};

const parseBackendTime = (str) => {
  if (!str) return null;
  if (str instanceof Date) return str;
  const [hh, mm] = String(str).split(":").map(Number);
  const d = new Date();
  d.setHours(hh || 0, mm || 0, 0, 0);
  return d;
};

export default function ServiceWindow({
  locationSlaDefaults,
  addData,
  initialServiceWindows = [],
  initialFollowsCustomerServiceWindow = true,
  onNextStep,
  onPrevStep,
}) {
  const [currentEditingIndex, setCurrentEditingIndex] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      serviceWindows: initialServiceWindows.map((w) => ({
        startTime: parseBackendTime(w.startTime),
        endTime: parseBackendTime(w.endTime),
        dayOfWeek: w.dayOfWeek,
        secondStartTime: parseBackendTime(w.secondStartTime),
        secondEndTime: parseBackendTime(w.secondEndTime),
      })),
      followsCustomerServiceWindow: initialFollowsCustomerServiceWindow,
      ...SERVICE_WINDOW_DEFAULT,
    },
  });

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: "serviceWindows",
  });

  const selectedDaysInGrid = useMemo(
    () => fields.map((f) => f.dayOfWeek),
    [fields],
  );

  const hasEverydayInGrid = selectedDaysInGrid.includes("Everyday");

  const hasWeekdaysInGrid = WEEKDAYS.some((d) =>
    selectedDaysInGrid.includes(d),
  );

  const hasOtherDaysInGrid = selectedDaysInGrid.some(
    (d) => d !== "Everyday" && !WEEKDAYS.includes(d),
  );

  const isDayDisabled = (day) => {
    if (currentEditingIndex !== null) return false;

    if (hasEverydayInGrid && day !== "Everyday") return true;

    if (hasWeekdaysInGrid && (day === "Everyday" || day === "Weekdays"))
      return true;

    if (day === "Weekdays" && (hasEverydayInGrid || hasOtherDaysInGrid))
      return true;

    return selectedDaysInGrid.includes(day);
  };

  const handleDayChange = (value) => {
    if (value.includes("Everyday")) {
      setValue("dayOfWeek", ["Everyday"], { shouldValidate: true });
      return;
    }

    if (value.includes("Weekdays")) {
      setValue("dayOfWeek", WEEKDAYS, { shouldValidate: true });
      return;
    }

    setValue(
      "dayOfWeek",
      value.filter((d) => d !== "Everyday" && d !== "Weekdays"),
      { shouldValidate: true },
    );
  };

  useEffect(() => {
    if (!addData) return;

    addData({
      followsCustomerServiceWindow:
        getValues("followsCustomerServiceWindow") || false,
      serviceWindows: fields.map((f) => ({
        startTime: toBackendTime(f.startTime),
        endTime: toBackendTime(f.endTime),
        dayOfWeek: f.dayOfWeek,
        secondStartTime: toBackendTime(f.secondStartTime),
        secondEndTime: toBackendTime(f.secondEndTime),
      })),
    });
  }, [fields, watch("followsCustomerServiceWindow")]);

  const selectedDay = watch("dayOfWeek")?.[0];

  useEffect(() => {
    if (!locationSlaDefaults?.serviceWindows) return;
    if (!selectedDay) return;
    if (currentEditingIndex !== null) return;

    const match = locationSlaDefaults.serviceWindows.find(
      (w) => w.dayOfWeek === selectedDay,
    );

    if (!match) return;

    setValue("startTime", parseBackendTime(match.startTime));
    setValue("endTime", parseBackendTime(match.endTime));
    setValue("secondStartTime", parseBackendTime(match.secondStartTime));
    setValue("secondEndTime", parseBackendTime(match.secondEndTime));
  }, [selectedDay, locationSlaDefaults, currentEditingIndex]);

  const submitInfo = (data) => {
    const base = {
      startTime: data.startTime,
      endTime: data.endTime,
      secondStartTime: data.secondStartTime,
      secondEndTime: data.secondEndTime,
    };

    if (currentEditingIndex !== null) {
      update(currentEditingIndex, {
        ...base,
        dayOfWeek: data.dayOfWeek[0],
      });
      setCurrentEditingIndex(null);
    } else {
      data.dayOfWeek
        .filter((day) => !selectedDaysInGrid.includes(day))
        .forEach((day) => append({ ...base, dayOfWeek: day }));
    }

    reset({
      ...SERVICE_WINDOW_DEFAULT,
      serviceWindows: [...getValues("serviceWindows")],
      followsCustomerServiceWindow: getValues("followsCustomerServiceWindow"),
    });
  };

  const startEdit = (dataItem, index) => {
    reset({
      dayOfWeek: [dataItem.dayOfWeek],
      startTime: dataItem.startTime,
      endTime: dataItem.endTime,
      secondStartTime: dataItem.secondStartTime,
      secondEndTime: dataItem.secondEndTime,
      serviceWindows: [...getValues("serviceWindows")],
      followsCustomerServiceWindow: getValues("followsCustomerServiceWindow"),
    });
    setCurrentEditingIndex(index);
  };

  const cancelEditing = () => {
    reset({
      ...SERVICE_WINDOW_DEFAULT,
      serviceWindows: [...getValues("serviceWindows")],
      followsCustomerServiceWindow: getValues("followsCustomerServiceWindow"),
    });
    setCurrentEditingIndex(null);
  };

  const formatTimeForGrid = (val) =>
    val instanceof Date
      ? val.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : "-";

  return (
    <form onSubmit={handleSubmit(submitInfo)}>
      {/* Top heading + navigation buttons */}
      <Flex w="100%" justify="space-between" align="center" mb={4}>
        <Heading size="md">Access Time</Heading>
        <Flex gap={3}>
          <Button
            type="button"
            onClick={() => {
              handleSubmit(submitInfo)(); // save current data
              if (onPrevStep) onPrevStep();
            }}
          >
            Back
          </Button>
          <Button
            type="button"
            onClick={() => {
              handleSubmit(submitInfo)(); // save current data
              if (onNextStep) onNextStep();
            }}
          >
            Next
          </Button>
        </Flex>
      </Flex>

      {/* Checkbox */}
      <Flex w="100%" mb={4}>
        {/* <FormControl mb={6}> */}
        <Controller
          name="followsCustomerServiceWindow"
          control={control}
          render={({ field }) => (
            <Checkbox
              {...field}
              isChecked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
            >
              Follow Customer Access Timing
            </Checkbox>
          )}
        />
        {/* </FormControl> */}
      </Flex>
      {/* DAY OF WEEK – MULTISELECT */}
      <FormControl isRequired mb={3} isInvalid={errors.dayOfWeek}>
        <FormLabel>Day of Week</FormLabel>
        <Controller
          name="dayOfWeek"
          control={control}
          rules={{ required: "Please select at least one day" }}
          render={({ field }) => (
            <MultiSelect
              data={daysOfWeek}
              filterable
              disabled={currentEditingIndex !== null}
              className="
            [&_.k-input-values]:max-h-[72px]
            [&_.k-input-values]:overflow-y-auto
            [&_.k-input-values::-webkit-scrollbar]:cursor-pointer
            [&_.k-chip]:whitespace-nowrap"
              value={field.value}
              onChange={(e) => handleDayChange(e.value)}
              itemRender={(li, itemProps) => {
                const disabled = isDayDisabled(itemProps.dataItem);
                return React.cloneElement(li, {
                  style: {
                    ...li.props.style,
                    opacity: disabled ? 0.5 : 1,
                    pointerEvents: disabled ? "none" : "auto",
                  },
                });
              }}
            />
          )}
        />
        {errors.dayOfWeek ? (
          <Text color="red.500" fontSize="xs">
            {errors.dayOfWeek.message}
          </Text>
        ) : null}
      </FormControl>

      {/* Start/End Time Inputs */}
      <FormControl isRequired mb={3}>
        <FormLabel>Start Time</FormLabel>
        <Controller
          name="startTime"
          control={control}
          rules={{ required: "Start Time is required" }}
          render={({ field }) => (
            <TimePicker
              value={field.value}
              onChange={(e) => field.onChange(e.value)}
              format="HH:mm"
            />
          )}
        />
      </FormControl>

      <FormControl isRequired mb={3}>
        <FormLabel>End Time</FormLabel>
        <Controller
          name="endTime"
          control={control}
          rules={{ required: "End Time is required" }}
          render={({ field }) => (
            <TimePicker
              value={field.value}
              onChange={(e) => field.onChange(e.value)}
              format="HH:mm"
            />
          )}
        />
      </FormControl>

      {/* Optional second time */}
      <FormControl mb={3}>
        <FormLabel>Second Start Time</FormLabel>
        <Controller
          name="secondStartTime"
          control={control}
          render={({ field }) => (
            <TimePicker
              value={field.value}
              onChange={(e) => field.onChange(e.value)}
              format="HH:mm"
            />
          )}
        />
      </FormControl>

      <FormControl mb={3}>
        <FormLabel>Second End Time</FormLabel>
        <Controller
          name="secondEndTime"
          control={control}
          render={({ field }) => (
            <TimePicker
              value={field.value}
              onChange={(e) => field.onChange(e.value)}
              format="HH:mm"
            />
          )}
        />
      </FormControl>

      {/* Grid */}
      <Grid data={fields}>
        <GridColumn
          title="Day"
          cell={({ dataItem }) => <td>{dataItem.dayOfWeek}</td>}
        />
        <GridColumn
          title="Start Time"
          cell={({ dataItem }) => (
            <td>{formatTimeForGrid(dataItem.startTime)}</td>
          )}
        />
        <GridColumn
          title="End Time"
          cell={({ dataItem }) => (
            <td>{formatTimeForGrid(dataItem.endTime)}</td>
          )}
        />
        <GridColumn
          title="Second Start"
          cell={({ dataItem }) => (
            <td>{formatTimeForGrid(dataItem.secondStartTime)}</td>
          )}
        />
        <GridColumn
          title="Second End"
          cell={({ dataItem }) => (
            <td>{formatTimeForGrid(dataItem.secondEndTime)}</td>
          )}
        />
        <GridColumn
          title="Edit"
          width={120}
          cell={({ dataItem, dataIndex }) => (
            <td onClick={() => startEdit(dataItem, dataIndex)}>
              <FaEdit />
            </td>
          )}
        />
        <GridColumn
          title="Delete"
          width={120}
          cell={({ dataIndex }) => (
            <td onClick={() => remove(dataIndex)}>
              <MdDeleteSweep color="red" />
            </td>
          )}
        />
      </Grid>

      {/* Add/Update button at bottom */}
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          gap: "16px",
        }}
      >
        {currentEditingIndex !== null && (
          <Button onClick={cancelEditing}>Cancel</Button>
        )}
        <Button type="submit">
          {currentEditingIndex !== null
            ? "Update Access Time"
            : "Add Access Time"}
        </Button>
      </div>
    </form>
  );
}
